/**
 * In welcher Sprache das CMS mit dem Benutzer spricht.
 *
 * NICHT die Sprache der Website, die bearbeitet wird — die steht in der
 * Seiten-Leiste (bei UnderWave EN und HE). Hier geht es um die Oberfläche
 * selbst: Knöpfe, Menüs, Meldungen.
 *
 * WARUM ES DAS BRAUCHT. Nahm arbeitet auf Englisch, und ein Kundenzugang aus
 * Israel oder Thailand kann mit „Zurück auf Entwurf" nichts anfangen (David,
 * 19.08.2026: „ist miracms nur in deutsch? wir brauchen das auch in english").
 *
 * DER VOREINGESTELLTE WERT BLEIBT DEUTSCH. Nach der Browsersprache zu gehen
 * wäre naheliegend und falsch: Davids Frau arbeitet täglich damit, und ein
 * englisch eingestellter Browser hätte ihr über Nacht eine fremde Oberfläche
 * hingestellt. Wer Englisch will, stellt es einmal um; die Wahl bleibt im
 * Browser gespeichert.
 */
import { useStorage } from '@vueuse/core'
import { watch, type Ref } from 'vue'
import i18n from '@/locales'
import { api } from '@/lib/api'

export type Sprache = 'de' | 'en'

export const SPRACH_SCHLUESSEL = 'miracms.sprache'

const gewaehlt = useStorage<Sprache>(SPRACH_SCHLUESSEL, 'de')

/** Die gespeicherte Wahl beim Start, ohne Vue — `locales/index.ts` braucht sie
 *  schon vor dem ersten Bauteil. */
export function gespeicherteSprache(): Sprache {
  try {
    return localStorage.getItem(SPRACH_SCHLUESSEL) === '"en"'
      || localStorage.getItem(SPRACH_SCHLUESSEL) === 'en' ? 'en' : 'de'
  } catch {
    return 'de'
  }
}

/* `locale` ist mit `legacy: false` ein schreibbarer Ref, der Typ von vue-i18n
   verrät das an dieser Stelle aber nicht. Ein Umweg statt einer Notlösung an
   zehn Stellen. */
const locale = i18n.global.locale as unknown as { value: Sprache }

watch(gewaehlt, (neu) => {
  locale.value = neu
  document.documentElement.setAttribute('lang', neu)
}, { immediate: true })

/**
 * Was der Server ueber diesen Zugang sagt, gilt.
 *
 * Er kennt zwei Dinge, die der Browser nicht kennt: die eigene Wahl dieses
 * Zugangs (die auf jedem Geraet gelten soll) und die Hauptsprache seines
 * Projekts. Ein Zugang von UnderWave bekommt so vom ersten Bild an Englisch,
 * ohne dass es jemand einstellt (David, 20.08.2026).
 *
 * Der Wert im Browser ist nur der Zwischenspeicher fuer den Moment vor der
 * ersten Antwort — sonst blitzte Deutsch auf und wuerde ersetzt.
 *
 * Gilt nur, was eine Sprache IST. Bis zum 08.10.2026 schickte die Anmeldung
 * `null`, das landete hier als Sprache, und vue-i18n fiel damit auf Deutsch
 * zurueck: Ein englischer Zugang sah nach dem Anmelden Deutsch, bis er neu
 * lud. Ohne brauchbare Angabe bleibt stehen, was da ist.
 */
function uebernehmeVomServer(sprache: Sprache | null | undefined): void {
  if (sprache !== 'de' && sprache !== 'en') return
  if (sprache === gewaehlt.value) return
  gewaehlt.value = sprache
}

/** Umstellen und beim Zugang merken. */
function setze(neu: Sprache): void {
  gewaehlt.value = neu
  /* Schlaegt das Merken fehl, bleibt die Wahl trotzdem in diesem Browser
     bestehen: Ein Fehlerfenster fuer eine Sprachumstellung waere laut fuer
     etwas, das der Benutzer gerade sieht und das offensichtlich geklappt hat. */
  void api.spracheSetzen(neu).catch(() => undefined)
}

export function useSprache(): {
  gewaehlt: Ref<Sprache>
  setze: (s: Sprache) => void
  uebernehmeVomServer: (s: Sprache | null | undefined) => void
} {
  return { gewaehlt, setze, uebernehmeVomServer }
}
